import { useEffect, useState } from "react";
import { useTasks } from "../context/TaskContext";
import { TaskCard } from "./TaskCard";
import { toast } from "sonner";
import Swal from "sweetalert2";

export const TaskList = ({ filter = "inbox", searchQuery = "" }) => {
    const { tasks, getTasks, loading, deleteMultipleTasks } = useTasks();

    // Estado para la paginación
    const [currentPage, setCurrentPage] = useState(1);
    const tasksPerPage = 6; // 👈 Número de tareas pendientes a mostrar por página

    // Estado para colapsar/desplegar tareas completadas
    const [showCompleted, setShowCompleted] = useState(false);

    useEffect(() => {
        getTasks();
    }, [getTasks]);

    // Resetea a la primera página cuando cambia el filtro o la búsqueda
    useEffect(() => {
        setCurrentPage(1);
    }, [filter, searchQuery]);

    function processTasks() {
        const todayStr = new Date().toISOString().split("T")[0];
        let filtered = [];

        // 1. Aplicar Búsqueda Global o Filtros por Sección
        if (searchQuery.trim() !== "") {
            const query = searchQuery.toLowerCase().trim();
            filtered = tasks.filter(
                (task) =>
                    task.name.toLowerCase().includes(query) ||
                    (task.description && task.description.toLowerCase().includes(query))
            );
        } else {
            switch (filter) {
                case "inbox":
                    filtered = tasks.filter((task) => task.project_id === null);
                    break;
                case "today":
                    filtered = tasks.filter((task) => task.due_date === todayStr);
                    break;
                case "upcoming":
                    filtered = tasks.filter((task) => task.due_date > todayStr);
                    break;
                default:
                    if (filter.startsWith("project-")) {
                        const projectIdStr = filter.split("-")[1];
                        filtered = tasks.filter((task) => String(task.project_id) === String(projectIdStr));
                    } else {
                        filtered = [...tasks];
                    }
                    break;
            }
        }

        // 2. SEPARAR TAREAS: Pendientes vs Completadas
        const activeTasks = filtered.filter((t) => !t.done);
        const completedTasks = filtered.filter((t) => t.done);

        // 3. Ordenar tareas pendientes por fecha/hora
        activeTasks.sort((a, b) => {
            const dateA = a.due_date || "9999-12-31";
            const dateB = b.due_date || "9999-12-31";
            if (dateA !== dateB) return dateA.localeCompare(dateB);

            const timeA = a.due_time || "23:59";
            const timeB = b.due_time || "23:59";
            return timeA.localeCompare(timeB);
        });

        return { activeTasks, completedTasks };
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <i className="fas fa-spinner animate-spin text-2xl text-slate-400"></i>
                <p className="text-sm text-slate-500 font-medium">Cargando tus tareas...</p>
            </div>
        );
    }

    const { activeTasks, completedTasks } = processTasks();

    // Lógica de Paginación para Tareas Pendientes
    const totalPages = Math.ceil(activeTasks.length / tasksPerPage);
    const indexOfLastTask = currentPage * tasksPerPage;
    const indexOfFirstTask = indexOfLastTask - tasksPerPage;
    const currentActiveTasks = activeTasks.slice(indexOfFirstTask, indexOfLastTask);

    const handleDeleteCompleted = (e) => {
        e.stopPropagation();
        const count = completedTasks.length;
        if (count === 0) return;

        Swal.fire({
            html: `
                <div class="flex flex-col items-center pt-2 pb-1">
                    <div class="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-500 mb-3 shadow-inner">
                        <i class="fas fa-broom text-2xl"></i>
                    </div>
                    <h3 class="text-xl font-extrabold text-slate-800 tracking-tight">¿Eliminar completadas?</h3>
                    <p class="text-xs text-slate-500 mt-1.5 leading-relaxed text-center">
                        Se eliminarán permanentemente las <strong class="text-red-600">${count} ${count === 1 ? "tarea completada" : "tareas completadas"}</strong> de esta sección.
                    </p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: '<i class="fas fa-trash-alt mr-1.5"></i> Sí, eliminar',
            cancelButtonText: "Cancelar",
            buttonsStyling: false,
            reverseButtons: true,
            padding: "1.5rem",
            backdrop: `rgba(15, 23, 42, 0.45) backdrop-filter backdrop-blur-sm`,
            customClass: {
                popup: "rounded-3xl shadow-2xl border border-slate-100 bg-white max-w-sm",
                confirmButton: "px-4 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-md shadow-red-500/25 transition-all duration-150 active:scale-95 ml-2 cursor-pointer",
                cancelButton: "px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all duration-150 active:scale-95 cursor-pointer"
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const idsToDelete = completedTasks.map((t) => t.id);
                    await deleteMultipleTasks(idsToDelete);
                    toast.success(`${count} ${count === 1 ? "tarea completada eliminada" : "tareas completadas eliminadas"}`);
                } catch (error) {
                    toast.error(`Error al eliminar tareas: ${error.message}`);
                }
            }
        });
    };

    if (activeTasks.length === 0 && completedTasks.length === 0) {
        return (
            <div className="text-center py-12 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 p-6">
                <div className="text-3xl mb-3">{searchQuery ? "🔍" : "📥"}</div>
                <h3 className="text-sm font-semibold text-slate-700">
                    {searchQuery ? `No hay resultados para "${searchQuery}"` : "No hay tareas aquí"}
                </h3>
                <p className="text-xs text-slate-500 mt-1">¡Tómate un descanso o crea una nueva tarea!</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* 1. SECCIÓN DE TAREAS PENDIENTES (PAGINADAS) */}
            <div className="space-y-3">
                {currentActiveTasks.map((task) => (
                    <TaskCard task={task} key={task.id} />
                ))}
            </div>

            {/* BARRA DE NAVEGACIÓN DE PÁGINAS */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-slate-200 pt-4 px-1">
                    <span className="text-xs text-slate-500 font-medium">
                        Página {currentPage} de {totalPages} ({activeTasks.length} pendientes)
                    </span>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <i className="fas fa-chevron-left mr-1"></i> Anterior
                        </button>

                        <button
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Siguiente <i className="fas fa-chevron-right ml-1"></i>
                        </button>
                    </div>
                </div>
            )}

            {/* 2. SECCIÓN DE TAREAS COMPLETADAS (COLAPSABLE CON BOTÓN DE ELIMINACIÓN LOCAL) */}
            {completedTasks.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between py-2 px-3 bg-slate-100/70 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setShowCompleted(!showCompleted)}
                            className="flex items-center space-x-2 text-xs font-bold text-slate-600 hover:text-slate-800 transition-colors flex-1 text-left"
                        >
                            <i className={`fas fa-chevron-${showCompleted ? "down" : "right"} text-slate-400 text-[10px]`}></i>
                            <span>Completadas ({completedTasks.length})</span>
                            <span className="text-[10px] text-slate-400 font-normal ml-1">
                                ({showCompleted ? "Ocultar" : "Mostrar"})
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={handleDeleteCompleted}
                            className="text-[11px] font-semibold text-red-500 hover:text-red-700 hover:bg-red-50 px-2.5 py-1 rounded-lg transition-all flex items-center space-x-1 border border-transparent hover:border-red-100 cursor-pointer"
                            title="Eliminar únicamente las tareas completadas de esta sección"
                        >
                            <i className="far fa-trash-alt text-[10px]"></i>
                            <span>Eliminar completadas</span>
                        </button>
                    </div>

                    {showCompleted && (
                        <div className="space-y-3 mt-3 animate-fadeIn">
                            {completedTasks.map((task) => (
                                <TaskCard task={task} key={task.id} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};